import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Mail, Plus, Send, Edit2, Trash2, Save, X, 
  Clock, CheckCircle, XCircle, Users, Calendar,
  Search, Eye
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchMarketingMessages,
  createMessage,
  updateMessage,
  deleteMessage,
  sendMarketingMessage
} from '../../../redux/features/marketingSlice';
import { fetchClientsAsync } from '../../../redux/features/clientSlice';
import { isMarketingChannelAllowed } from '../../../utils/packageLimits';

const EmailView = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { messages, loading } = useSelector((state) => state.marketing);
  const { clients } = useSelector((state) => state.client);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    content: '',
    scheduledAt: ''
  });

  useEffect(() => {
    if (!isMarketingChannelAllowed('email', user)) return;
    dispatch(fetchMarketingMessages('email'));
    dispatch(fetchClientsAsync());
  }, [dispatch, user]);

  if (!isMarketingChannelAllowed('email', user)) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-600 bg-amber-900/20 p-6 text-amber-100">
          <h2 className="text-xl font-semibold mb-2">Mejl marketing</h2>
          <p>Ova opcija je dostupna samo u Business paketu.</p>
        </div>
      </div>
    );
  }

  const emailMessages = messages.filter(msg => msg.type === 'email');

  const filteredMessages = emailMessages.filter(msg =>
    msg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRecipientToggle = (clientId) => {
    setSelectedRecipients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    const clientsWithEmail = clients.filter(c => c.email).map(c => c._id);
    if (selectedRecipients.length === clientsWithEmail.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(clientsWithEmail);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Naslov i sadržaj su obavezni');
      return;
    }

    if (selectedRecipients.length === 0) {
      toast.error('Izaberite bar jednog primaoca');
      return;
    }

    const messageData = {
      type: 'email',
      title: formData.title,
      subject: formData.subject || formData.title,
      content: formData.content,
      recipientIds: selectedRecipients,
      scheduledAt: formData.scheduledAt || null
    };

    try {
      if (editingMessage) {
        await dispatch(updateMessage({ id: editingMessage._id, messageData })).unwrap();
        setEditingMessage(null);
      } else {
        await dispatch(createMessage(messageData)).unwrap();
      }
      
      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      title: message.title,
      subject: message.subject || message.title,
      content: message.content,
      scheduledAt: message.scheduledAt ? new Date(message.scheduledAt).toISOString().slice(0, 16) : ''
    });
    setSelectedRecipients(message.recipients.map(r => r.clientId?._id || r.clientId).filter(Boolean));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovu poruku?')) {
      try {
        await dispatch(deleteMessage(id)).unwrap();
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const handleSend = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da pošaljete ovu poruku?')) {
      try {
        await dispatch(sendMarketingMessage(id)).unwrap();
        dispatch(fetchMarketingMessages('email'));
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subject: '',
      content: '',
      scheduledAt: ''
    });
    setSelectedRecipients([]);
    setEditingMessage(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'sending':
        return <Clock className="h-5 w-5 text-yellow-400 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'scheduled':
        return <Calendar className="h-5 w-5 text-blue-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'sent': return 'Poslato';
      case 'sending': return 'Šalje se';
      case 'failed': return 'Neuspešno';
      case 'scheduled': return 'Zakazano';
      case 'draft': return 'Nacrt';
      default: return status;
    }
  };

  const clientsWithEmail = clients.filter(c => c.email);

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-800/90 rounded-xl shadow-2xl p-5 border border-gray-700/50 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/20 rounded-xl p-3 border border-blue-500/30 shadow-lg">
                <Mail className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Mejl Marketing</h1>
                <p className="text-gray-400 text-sm mt-0.5">Slanje email kampanja vašim klijentima</p>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              <Plus className="h-4 w-4" />
              {showForm ? 'Otkaži' : 'Nova Poruka'}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Pretraži poruke..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-700/50 text-white rounded-lg border border-gray-600/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-gradient-to-br from-gray-800 to-gray-800/90 rounded-xl shadow-2xl p-5 border border-gray-700/50 backdrop-blur-sm">
            <h2 className="text-lg font-semibold text-white mb-4">
              {editingMessage ? 'Izmeni Poruku' : 'Nova Email Poruka'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  Naslov *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-gray-700/50 text-white rounded-lg border border-gray-600/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  Predmet
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-gray-700/50 text-white rounded-lg border border-gray-600/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="Ako se ne unese, koristi se naslov"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  Sadržaj *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full px-3 py-2 text-sm bg-gray-700/50 text-white rounded-lg border border-gray-600/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  Zakazano vreme (opciono)
                </label>
                <input
                  type="datetime-local"
                  name="scheduledAt"
                  value={formData.scheduledAt}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-gray-700/50 text-white rounded-lg border border-gray-600/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* Recipient Selector */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-300">
                    Primaoci * ({selectedRecipients.length} izabrano)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowRecipientSelector(!showRecipientSelector)}
                    className="text-blue-400 hover:text-blue-300 text-xs transition-colors"
                  >
                    {showRecipientSelector ? 'Sakrij' : 'Izaberi Primaoce'}
                  </button>
                </div>
                
                {showRecipientSelector && (
                  <div className="bg-gray-700/50 rounded-lg p-3 max-h-60 overflow-y-auto border border-gray-600/50 shadow-inner">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-300">
                        {clientsWithEmail.length} klijenata sa email adresom
                      </span>
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {selectedRecipients.length === clientsWithEmail.length ? 'Poništi sve' : 'Izaberi sve'}
                      </button>
                    </div>
                    <div className="space-y-1.5">
                      {clientsWithEmail.map(client => (
                        <label
                          key={client._id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-600/50 rounded cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(client._id)}
                            onChange={() => handleRecipientToggle(client._id)}
                            className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-white text-xs">
                            {client.name} ({client.email})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  <Save className="h-4 w-4" />
                  {editingMessage ? 'Sačuvaj Izmene' : 'Sačuvaj'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  <X className="h-4 w-4" />
                  Otkaži
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Messages List */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-800/90 rounded-xl shadow-2xl p-5 border border-gray-700/50 backdrop-blur-sm">
          <h2 className="text-lg font-semibold text-white mb-3">Sačuvane Poruke</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-400 mx-auto"></div>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Nema sačuvanih poruka
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map((message) => (
                <div
                  key={message._id}
                  className="bg-gray-700/50 rounded-lg p-3 border border-gray-600/50 hover:border-blue-500/50 hover:shadow-lg transition-all backdrop-blur-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h3 className="text-sm font-semibold text-white">{message.title}</h3>
                        {getStatusIcon(message.status)}
                        <span className="text-xs text-gray-400">{getStatusText(message.status)}</span>
                      </div>
                      <p className="text-gray-300 text-xs mb-1.5">
                        <span className="font-medium">Predmet:</span> {message.subject || message.title}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {message.totalRecipients} primaoca
                        </div>
                        {message.sentAt && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Poslato: {new Date(message.sentAt).toLocaleString('sr-RS')}
                          </div>
                        )}
                        {message.scheduledAt && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Zakazano: {new Date(message.scheduledAt).toLocaleString('sr-RS')}
                          </div>
                        )}
                        {message.status === 'sent' && (
                          <>
                            <span className="text-green-400">
                              ✓ {message.sentCount} poslato
                            </span>
                            {message.failedCount > 0 && (
                              <span className="text-red-400">
                                ✗ {message.failedCount} neuspešno
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5 ml-3">
                      {message.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleEdit(message)}
                            className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-all shadow-sm hover:shadow-md"
                            title="Izmeni"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleSend(message._id)}
                            className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-all shadow-sm hover:shadow-md"
                            title="Pošalji"
                          >
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {message.status === 'scheduled' && (
                        <button
                          onClick={() => handleSend(message._id)}
                          className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-all shadow-sm hover:shadow-md"
                          title="Pošalji sada"
                        >
                          <Send className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(message._id)}
                        className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-all shadow-sm hover:shadow-md"
                        title="Obriši"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailView;
